import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { detectTimeZone, isValidTimeZone, resolveTimeZone, zoneAbbreviation } from '@csa/shared';
import { api } from './api.js';
import { SESSION_QUERY_KEY, type SessionResponse } from '../auth/useSession.js';

/**
 * One place that decides what zone a date is rendered in, and one function
 * that renders it.
 *
 * Before this existed, four admin pages each carried a private copy of a
 * `formatSydney` helper with `timeZone: 'Australia/Sydney'` hardcoded. Four
 * copies of a constant is four places to edit and three places to forget,
 * and the constant was wrong for anyone not in that zone -- an incident
 * timeline read in Perth was two hours out with nothing on screen to say so.
 *
 * The zone is resolved once here, from the user's saved preference falling
 * back to whatever the browser reports. Everything below takes it from
 * context, so there is no call site that can accidentally opt out.
 *
 * Two rules this module enforces by construction:
 *
 *   * **Every rendered timestamp is labelled** with its zone. `format` has
 *     no option to omit it, because a timeline screenshot with no zone on it
 *     is one somebody will misread. `formatBare` exists for the case where
 *     the label is already on the column header, and it returns the label
 *     separately rather than discarding it.
 *   * **The UTC value stays reachable.** `titleFor` produces the full ISO
 *     instant for a `title` attribute, so hovering any date in the console
 *     shows the unambiguous value an export would contain.
 */

interface TimeZoneContextValue {
  /** The zone actually in use, after preference and browser are considered. */
  readonly timeZone: string;
  /** The saved preference, or `null` when following the browser. */
  readonly preference: string | null;
  /** What the browser reports, shown in settings so the default is visible. */
  readonly browserZone: string;
  /** True when no preference is saved and the browser is being followed. */
  readonly isFollowingBrowser: boolean;
  /** Date and time, with the zone appended. */
  readonly format: (iso: string | Date | null | undefined) => string;
  /** Date and time, with the zone returned separately. */
  readonly formatBare: (iso: string | Date | null | undefined) => {
    value: string;
    zone: string;
  };
  /** Date only. Used in table columns where the time is noise. */
  readonly formatDate: (iso: string | Date | null | undefined) => string;
  /** The full UTC instant, for a `title` attribute. */
  readonly titleFor: (iso: string | Date | null | undefined) => string;
  readonly setPreference: (zone: string | null) => Promise<void>;
  readonly isSaving: boolean;
}

const TimeZoneContext = createContext<TimeZoneContextValue | null>(null);

/**
 * An em dash, not an empty string.
 *
 * A missing date rendered as nothing leaves a blank cell that reads as a
 * layout bug; one that reads as an em dash reads as "there is no value here",
 * which is the fact.
 */
const ABSENT = '—';

function toDate(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  /**
   * `new Date('nonsense')` produces an Invalid Date rather than throwing, and
   * formatting one yields the string "Invalid Date" in the middle of a table.
   * Treated as absent instead: a malformed timestamp from the API is a bug to
   * find in the logs, not one to render.
   */
  return Number.isNaN(date.getTime()) ? null : date;
}

export function TimeZoneProvider({
  preference,
  children,
}: {
  preference: string | null;
  children: ReactNode;
}): ReactElement {
  const queryClient = useQueryClient();

  /**
   * Detected once per mount rather than per render. It cannot change without
   * the operating system changing, and calling into `Intl` for every cell of
   * a five-hundred-row audit table is measurable.
   */
  const browserZone = useMemo(() => detectTimeZone(), []);
  const timeZone = useMemo(
    () => resolveTimeZone(preference, browserZone),
    [preference, browserZone],
  );

  /**
   * The formatters are built once for the zone, not once per call.
   * `Intl.DateTimeFormat` construction is the expensive part -- roughly two
   * orders of magnitude more than a `format` call -- and the old per-page
   * helpers constructed a new one for every row they rendered.
   */
  const formatters = useMemo(
    () => ({
      dateTime: new Intl.DateTimeFormat(undefined, {
        timeZone,
        dateStyle: 'short',
        timeStyle: 'short',
      }),
      date: new Intl.DateTimeFormat(undefined, { timeZone, dateStyle: 'medium' }),
    }),
    [timeZone],
  );

  const formatBare = useCallback(
    (value: string | Date | null | undefined) => {
      const date = toDate(value);
      if (!date) return { value: ABSENT, zone: '' };
      return {
        value: formatters.dateTime.format(date),
        // Per instant, not per zone: during the hour after a daylight-saving
        // change one row is AEST and the next is AEDT, and that difference is
        // the only thing that makes the ordering legible.
        zone: zoneAbbreviation(date, timeZone),
      };
    },
    [formatters, timeZone],
  );

  const format = useCallback(
    (value: string | Date | null | undefined) => {
      const { value: rendered, zone } = formatBare(value);
      return zone ? `${rendered} ${zone}` : rendered;
    },
    [formatBare],
  );

  const formatDate = useCallback(
    (value: string | Date | null | undefined) => {
      const date = toDate(value);
      return date ? formatters.date.format(date) : ABSENT;
    },
    [formatters],
  );

  const titleFor = useCallback((value: string | Date | null | undefined) => {
    const date = toDate(value);
    return date ? `${date.toISOString()} (UTC)` : 'No value';
  }, []);

  const mutation = useMutation({
    mutationFn: async (zone: string | null) => {
      if (zone !== null && !isValidTimeZone(zone)) {
        // Caught here as well as at the API, because the failure the user
        // should see for an unknown zone is an immediate refusal, not a
        // round-trip that returns 400.
        throw new Error(`${zone} is not a time zone this browser recognises`);
      }
      await api.put('/api/me/preferences', { timeZone: zone });
      return zone;
    },
    onSuccess: (zone) => {
      /**
       * The session query holds the preference, so it is patched in place
       * rather than invalidated. Invalidating would refetch `/api/me` with
       * the sign-in header and re-render the whole shell to change a date
       * format.
       */
      queryClient.setQueryData<SessionResponse>(SESSION_QUERY_KEY, (current) =>
        current ? { ...current, preferences: { timeZone: zone } } : current,
      );
    },
  });

  const setPreference = useCallback(
    async (zone: string | null) => {
      await mutation.mutateAsync(zone);
    },
    [mutation],
  );

  const value = useMemo<TimeZoneContextValue>(
    () => ({
      timeZone,
      preference,
      browserZone,
      isFollowingBrowser: preference === null,
      format,
      formatBare,
      formatDate,
      titleFor,
      setPreference,
      isSaving: mutation.isPending,
    }),
    [
      timeZone,
      preference,
      browserZone,
      format,
      formatBare,
      formatDate,
      titleFor,
      setPreference,
      mutation.isPending,
    ],
  );

  return <TimeZoneContext.Provider value={value}>{children}</TimeZoneContext.Provider>;
}

/**
 * Throws outside a provider rather than falling back to UTC.
 *
 * A silent fallback would mean a component rendered outside the tree shows
 * plausible-looking times in the wrong zone, and the bug would be found by a
 * user comparing two screens rather than by anybody reading this code.
 */
export function useTimeZone(): TimeZoneContextValue {
  const ctx = useContext(TimeZoneContext);
  if (!ctx) throw new Error('useTimeZone must be used inside a TimeZoneProvider');
  return ctx;
}
